import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Material UI
import { Button, Chip, Grid, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// redux additions
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as caseActions from './CaseActions';

// Components
import Trustness from '../../Actions/Trustness';
import Categories from './Categories';
import InfoTable from '../../Shared/InfoTable';
import BasicInfo from './BasicInfo';
import DetailedInfo from './DetailedInfo';
import { Timeline } from './Timeline';

// Utils
import utils from '../../../utils/utils';

// Styles
import CaseInfoStyles from '../../../assets/jss/Components/CaseInfoStyles';
import { statusTitle } from '../DataSources/ImportWizard/helpers/StatusTitle';
import IconButton from '@mui/material/IconButton';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Paper from '@mui/material/Paper';

const tabs = {
    info: {
        label: 'Case Information',
    },
    imports: {
        label: 'File Imports',
    },
};

export const CaseInfo = ({
    files,
    area,
    caseId,
    width,
    handleCloseCase,
    ...props
}) => {
    const { classes } = CaseInfoStyles();
    const dispatch = useDispatch();
    const location = useLocation();
    const caseInfo = useSelector((state: any) => state.cases.selectedCaseData);
    const trustData = useSelector((state: any) => state.cases);
    const [workspaceId, setWorkspaceId] = React.useState('');
    const [activeTab, setActiveTab] = React.useState('info');

    useEffect(() => {
        if (workspaceId && caseId && caseInfo && caseInfo.data) {
            dispatch(caseActions?.checkCaseTrustness(workspaceId, caseId));
            if (
                caseInfo.data &&
                caseInfo.data.imports &&
                caseInfo.data.imports.length > 0
            ) {
                let fileIds = caseInfo.data.imports.map(({ id }) => id);
                dispatch(caseActions?.checkFileTrustness(fileIds));
            }
        }
    }, [caseInfo, workspaceId, caseId, dispatch]);

    useEffect(() => {
        const workspace = location.pathname.split('/').pop();
        setWorkspaceId(workspace);
    }, [location]);

    const handleBackClick = () => {
        handleCloseCase();
    };

    const handleFileDownload = (e, id, fileName) => {
        e.stopPropagation();
        dispatch(caseActions?.getFile(id, fileName));
    };

    const renderTabContent = (caseInfo) => {
        let columns = [];
        switch (activeTab) {
            case 'imports':
                columns = [
                    'id',
                    'fileName',
                    'added',
                    'added__customSort',
                    'status',
                    'trusted',
                    'download',
                ];
                return (
                    <InfoTable
                        activeTab={activeTab}
                        list={caseInfo.imports
                            .sort((a, b) => {
                                if (a.added < b.added) return 1;
                                if (a.added > b.added) return -1;
                                return 0;
                            })
                            .map((f, i) => ({
                                ...f,
                                added: utils.formatDate(f.added, 'L LTS'),
                                added__customSort: f.added,
                                status: (
                                    <Tooltip
                                        title={utils.toTitleText(
                                            f.status.toLowerCase()
                                        )}
                                    >
                                        {statusTitle(f.status, 'table')}
                                    </Tooltip>
                                ),
                                download: (
                                    <IconButton
                                        size={'small'}
                                        onClick={(e) =>
                                            handleFileDownload(
                                                e,
                                                f.id,
                                                f.fileName
                                            )
                                        }
                                    >
                                        <FileDownloadIcon />
                                    </IconButton>
                                ),
                                trusted: (
                                    <Trustness
                                        type="file"
                                        score={
                                            trustData.files &&
                                            trustData.files[f.id] &&
                                            trustData.files[f.id].trust &&
                                            trustData.files[f.id].trust.score
                                        }
                                        verified={
                                            trustData.files &&
                                            trustData.files[f.id] &&
                                            trustData.files[f.id].trust &&
                                            trustData.files[f.id].trust.verified
                                        }
                                        checking={
                                            trustData.files &&
                                            trustData.files[f.id] &&
                                            trustData.files[f.id].checkingTrust
                                        }
                                        onExpertVerification={() =>
                                            window.open(
                                                `https://ledger.hashlog.io/tx/${f.transactionId}`,
                                                '_blank'
                                            )
                                        }
                                        disabled={false}
                                    />
                                ),
                            }))}
                        columns={columns}
                        toolbar={(id) => () => {
                            const file = caseInfo.imports.find(
                                (f) => f.id === id
                            );
                            return (
                                <Grid
                                    container
                                    justifyContent="flex-end"
                                    spacing={2}
                                >
                                    <Grid item>
                                        <Button
                                            color="primary"
                                            variant="outlined"
                                            onClick={() =>
                                                dispatch(
                                                    caseActions?.getFile(
                                                        id,
                                                        file.fileName
                                                    )
                                                )
                                            }
                                        >
                                            Download File
                                        </Button>
                                    </Grid>
                                </Grid>
                            );
                        }}
                    />
                );

            case 'info':
            default:
                return (
                    <Grid container>
                        <Grid item xs={12}>
                            <DetailedInfo info={caseInfo} />
                        </Grid>
                    </Grid>
                );
        }
    };

    const renderCaseInfo = (caseInfo, error) => {
        if (!caseInfo && error) {
            return <Typography>{error.message}</Typography>;
        }

        if (caseInfo) {
            return (
                <Grid container spacing={1}>
                    <Grid
                        item
                        container
                        justifyContent="space-between"
                        spacing={2}
                        alignItems={'center'}
                    >
                        <Grid item>
                            {/*@ts-ignore*/}
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                sx={{ marginRight: 1 }}
                                size={'small'}
                                onClick={handleBackClick}
                            >
                                Back to Case List
                            </Button>
                            <span className={classes.title}>
                                Case ID <b>#{caseId}</b>
                            </span>
                        </Grid>
                        <Grid item>
                            <Grid
                                container
                                spacing={1}
                                alignItems="center"
                                style={{ minHeight: 60, padding: 4 }}
                            >
                                <Grid item>
                                    <Trustness
                                        type="case data"
                                        score={
                                            trustData[workspaceId] &&
                                            trustData[workspaceId][caseId] &&
                                            trustData[workspaceId][caseId]
                                                .trust &&
                                            trustData[workspaceId][caseId].trust
                                                .score
                                        }
                                        verified={
                                            trustData[workspaceId] &&
                                            trustData[workspaceId][caseId] &&
                                            trustData[workspaceId][caseId]
                                                .trust &&
                                            trustData[workspaceId][caseId].trust
                                                .verified
                                        }
                                        checking={
                                            trustData[workspaceId] &&
                                            trustData[workspaceId][caseId] &&
                                            trustData[workspaceId][caseId]
                                                .checkingTrust
                                        }
                                        onExpertVerification={() =>
                                            window.open(
                                                `https://ledger.hashlog.io/tx/${caseInfo.transactionId}`,
                                                '_blank'
                                            )
                                        }
                                        disabled={false}
                                    />
                                </Grid>
                                {caseInfo.lastModified && (
                                    <Grid item>
                                        <Chip
                                            label={`Modified on: ${utils.formatDate(
                                                caseInfo.lastModified,
                                                'L LTS'
                                            )}`}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper className={classes.container} elevation={0}>
                                <Typography
                                    variant={'body2'}
                                    sx={{ fontWeight: 'bold', pb: 3 }}
                                >
                                    Patient Journey Timeline
                                </Typography>
                                <Timeline info={caseInfo} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Grid item container spacing={2}>
                                        <Grid item style={{ maxWidth: '100%' }}>
                                            <BasicInfo info={caseInfo} />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                            <Paper
                                                className={classes.container}
                                                elevation={0}
                                            >
                                                <Categories
                                                    orientation="horizontal"
                                                    initiallyActive={activeTab}
                                                    onChange={(id) =>
                                                        setActiveTab(id)
                                                    }
                                                    list={Object.keys(tabs)
                                                        .map((t) => {
                                                            const result = {
                                                                id: t,
                                                                label: tabs[t]
                                                                    .label,
                                                                count: undefined,
                                                                disabled:
                                                                    undefined,
                                                            };

                                                            if (t !== 'info') {
                                                                if (
                                                                    caseInfo[t]
                                                                ) {
                                                                    result.count =
                                                                        caseInfo[
                                                                            t
                                                                        ].length;
                                                                    result.disabled =
                                                                        !caseInfo[
                                                                            t
                                                                        ]
                                                                            .length;
                                                                } else {
                                                                    return null;
                                                                }
                                                            }

                                                            return result;
                                                        })
                                                        .filter(
                                                            (i) => i !== null
                                                        )}
                                                    loading={undefined}
                                                />
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12}>
                                            {renderTabContent(caseInfo)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            );
        }
        return null;
    };

    return (
        <>
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                spacing={3}
            >
                {caseInfo && caseInfo.data && (
                    <Grid item>{renderCaseInfo(caseInfo.data, null)}</Grid>
                )}
            </Grid>
        </>
    );
};

const mapStateToProps = (state) => ({
    files: state.cases && state.cases.files,
    areas: (state.userAccess && state.userAccess.areas) || [],
    loadingAreas: state.userAccess && state.userAccess.loading,
});

const mapDispatchToProps = (dispatch) => ({
    caseActions: bindActionCreators({ ...caseActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(CaseInfo);
